import { useState, useEffect, useCallback } from "react";
import useFetch from "Hooks/useFetch";
import usePostData from "Hooks/usePostData";

// Loads the learner-safe quiz for a target and submits attempts. Works for both
// scopes: lesson → { scope:"lesson", course, lessonId }, course → { scope:"course", course }.
const useLessonQuiz = ({ scope = "lesson", courseId, lessonId, token, profileId }) => {
  const { fetchData } = useFetch();
  const { postData } = usePostData();
  // Fall back to the persisted token so a submit never goes out unauthenticated
  // just because the context value hasn't propagated to this prop yet.
  const authToken =
    token || (typeof window !== "undefined" ? localStorage.getItem("token") : null);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (!courseId) return;
    if (scope === "lesson" && !lessonId) return;
    setResult(null);
    setQuiz(null);
    setLoading(true);
    const query =
      scope === "course"
        ? `quizzes?scope=course&course=${courseId}`
        : `quizzes?scope=lesson&course=${courseId}&lessonId=${lessonId}`;
    fetchData(query, authToken)
      .then((res) => {
        if (active) setQuiz(res?.data || null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // fetchData is stable enough; adding it re-runs the effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, courseId, lessonId, token]);

  // answers: { [questionId]: value } → array the backend expects.
  const submit = useCallback(
    async (answers) => {
      if (!quiz?._id) return;
      setSubmitting(true);
      setError(null);
      try {
        const payload = {
          profileId: profileId || null,
          answers: Object.entries(answers).map(([questionId, value]) => ({
            questionId,
            value,
          })),
        };
        const res = await postData(
          `quizzes/${quiz._id}/submit`,
          payload,
          authToken
        );
        // A graded result always carries `total`; anything else is a failure.
        if (res?.data && typeof res.data.total === "number") {
          setResult(res.data);
        } else {
          // Surface the server's real message (e.g. auth) for diagnosis.
          setError(res?.message || "submit_failed");
        }
      } catch (e) {
        setError(e?.message || "submit_failed");
      } finally {
        setSubmitting(false);
      }
    },
    [quiz, authToken, profileId, postData]
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { quiz, loading, result, submitting, error, submit, reset };
};

export default useLessonQuiz;
